import { computed, makeObservable } from 'mobx'

import { ISiteLocation, LocationType } from '~/client/graph'
import {
  getLocationRootKind,
  isLocationOfKind,
  LocationKindValue,
  rootOfKind,
} from '~/client/src/shared/resources/domain/locationKind'

import LocationAttributeBase from '../../models/LocationObjects/LocationAttributeBase'
import LocationBase from '../../models/LocationObjects/LocationBase'
import ProjectLocation from '../../models/LocationObjects/ProjectLocation'
import { sortAttributes } from '../../utils/sortingFunctions'
import AreasStore from './Areas.store'
import BuildingsStore from './Buildings.store'
import GatesStore from './Gates.store'
import InteriorDoorsStore from './InteriorDoors.store'
import InteriorPathsStore from './InteriorPaths.store'
import LevelsStore from './Levels.store'
import LocationIntegrationsStore from './LocationIntegrations.store'
import LogisticsObjectsStore from './LogisticsObjects.store'
import OffloadingEquipmentsStore from './OffloadingEquipments.store'
import OtherLocationsStore from './OtherLocations.store'
import ProjectLocationsStore from './ProjectLocations.store'
import RoutesStore from './Routes.store'
import StagingsStore from './Stagings.store'
import VerticalObjectsStore from './VerticalObjects.store'
import ZonesStore from './Zones.store'

export default class LocationAttributesStore {
  public constructor(
    public readonly gatesStore: GatesStore,
    public readonly zonesStore: ZonesStore,
    public readonly buildingsStore: BuildingsStore,
    public readonly routesStore: RoutesStore,
    public readonly offloadingEquipmentsStore: OffloadingEquipmentsStore,
    public readonly stagingsStore: StagingsStore,
    public readonly interiorDoorsStore: InteriorDoorsStore,
    public readonly interiorPathsStore: InteriorPathsStore,
    public readonly levelsStore: LevelsStore,
    public readonly areasStore: AreasStore,
    public readonly logisticsObjectsStore: LogisticsObjectsStore,
    public readonly locationIntegrationsStore: LocationIntegrationsStore,
    public readonly verticalObjectsStore: VerticalObjectsStore,
    public readonly projectLocationsStore: ProjectLocationsStore,
    public readonly otherLocationsStore: OtherLocationsStore,

    private readonly storeByLocationTypeMap = {
      [LocationType.Gate]: gatesStore,
      [LocationType.Zone]: zonesStore,
      [LocationType.Building]: buildingsStore,
      [LocationType.Route]: routesStore,
      [LocationType.OffloadingEquipment]: offloadingEquipmentsStore,
      [LocationType.Staging]: stagingsStore,
      [LocationType.InteriorDoor]: interiorDoorsStore,
      [LocationType.InteriorPath]: interiorPathsStore,
      [LocationType.Level]: levelsStore,
      [LocationType.Area]: areasStore,
      [LocationType.LogisticsObject]: logisticsObjectsStore,
      [LocationType.VerticalObject]: verticalObjectsStore,
      [LocationType.Integration]: locationIntegrationsStore,
      [LocationType.ProjectLocation]: projectLocationsStore,
      // The collapsed type the catch-all's rows report (STX1-597).
      [LocationType.Location]: otherLocationsStore,
    },
  ) {
    makeObservable(this)
  }

  @computed
  public get projectLocation(): ProjectLocation {
    return this.projectLocationsStore.list?.[0]
  }

  @computed
  public get allAttributes(): LocationBase[] {
    return [
      ...this.projectLocationsStore.list,
      ...this.buildingsStore.list,
      ...this.zonesStore.list,
      ...this.gatesStore.list,
      ...this.routesStore.list,
      ...this.offloadingEquipmentsStore.list,
      ...this.stagingsStore.list,
      ...this.interiorDoorsStore.list,
      ...this.interiorPathsStore.list,
      ...this.levelsStore.list,
      ...this.areasStore.list,
      ...this.logisticsObjectsStore.list,
      ...this.locationIntegrationsStore.list,
      ...this.verticalObjectsStore.list,
      ...this.otherLocationsStore.list,
    ]
  }

  public getSortedAllAttributes(
    sortAttributes: (a: LocationBase, b: LocationBase) => number,
  ) {
    return [
      ...this.projectLocationsStore.list.sort(sortAttributes),
      ...this.buildingsStore.list.sort(sortAttributes),
      ...this.zonesStore.list.sort(sortAttributes),
      ...this.gatesStore.list.sort(sortAttributes),
      ...this.routesStore.list.sort(sortAttributes),
      ...this.offloadingEquipmentsStore.list.sort(sortAttributes),
      ...this.stagingsStore.list.sort(sortAttributes),
      ...this.interiorDoorsStore.list.sort(sortAttributes),
      ...this.interiorPathsStore.list.sort(sortAttributes),
      ...this.levelsStore.list,
      ...this.areasStore.list.sort(sortAttributes),
      ...this.logisticsObjectsStore.list.sort(sortAttributes),
      ...this.locationIntegrationsStore.list.sort(sortAttributes),
      ...this.verticalObjectsStore.list.sort(sortAttributes),
      ...this.otherLocationsStore.list.sort(sortAttributes),
    ]
  }

  // Hierarchy order is what makes the first of a globe's assigned locations its LBS tag.
  @computed
  private get sortedAllAttributes(): LocationBase[] {
    return this.getSortedAllAttributes(sortAttributes)
  }

  public globeAssignedLocations(globeId: string): LocationBase[] {
    return this.sortedAllAttributes.filter(l => l.isGlobeAssigned(globeId))
  }

  @computed
  public get hierarchyAllAttributesMap(): {
    [key: string]: LocationBase
  } {
    return this.allAttributes.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }

  // Walks parent.parentId back to (but not including) the implicit
  // ProjectLocation root; returns root-first. Starting attr is the deepest.
  public getAncestryChain = (attr: LocationBase): LocationBase[] => {
    const map = this.hierarchyAllAttributesMap
    const chain: LocationBase[] = [attr]
    let cursor: LocationBase = attr
    // Cycle guard: corrupt parent pointers would otherwise lock the UI
    // (this runs per-map-view on every MapViewsSidebarContent render).
    const visited = new Set<string>([attr.id])
    while (cursor.parent?.parentId) {
      const parent = map[cursor.parent.parentId]
      if (!parent || parent.is(LocationType.ProjectLocation)) break
      if (visited.has(parent.id)) break
      visited.add(parent.id)
      chain.push(parent)
      cursor = parent
    }
    return chain.reverse()
  }

  @computed
  public get allLocationsWithoutEquipmentMap(): {
    [key: string]: LocationBase
  } {
    return this.allLocationsWithoutEquipment.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }

  @computed
  public get allItemsMapWithDeleted(): {
    [key: string]: LocationBase
  } {
    return this.allAttributesWithDeletedItems.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }

  @computed
  public get allLocationsWithoutEquipment() {
    return [
      ...this.projectLocationsStore.list,
      ...this.buildingsStore.list,
      ...this.zonesStore.list,
      ...this.gatesStore.list,
      ...this.routesStore.list,
      ...this.stagingsStore.list,
      ...this.interiorDoorsStore.list,
      ...this.interiorPathsStore.list,
      ...this.levelsStore.list,
      ...this.areasStore.list,
      ...this.logisticsObjectsStore.list,
      ...this.locationIntegrationsStore.list,
      ...this.verticalObjectsStore.list,
      ...this.otherLocationsStore.list,
    ]
  }

  @computed
  public get allAttributesWithDeletedItems(): LocationBase[] {
    return [
      ...this.projectLocationsStore.listWithDeletedItems,
      ...this.buildingsStore.listWithDeletedItems,
      ...this.zonesStore.listWithDeletedItems,
      ...this.gatesStore.listWithDeletedItems,
      ...this.routesStore.listWithDeletedItems,
      ...this.offloadingEquipmentsStore.listWithDeletedItems,
      ...this.stagingsStore.listWithDeletedItems,
      ...this.interiorDoorsStore.listWithDeletedItems,
      ...this.interiorPathsStore.listWithDeletedItems,
      ...this.levelsStore.listWithDeletedItems,
      ...this.areasStore.listWithDeletedItems,
      ...this.logisticsObjectsStore.listWithDeletedItems,
      ...this.locationIntegrationsStore.listWithDeletedItems,
      ...this.verticalObjectsStore.listWithDeletedItems,
      ...this.otherLocationsStore.listWithDeletedItems,
    ]
  }

  @computed
  public get attributesWithDeletedWithoutEquipment(): LocationBase[] {
    return [
      ...this.projectLocationsStore.listWithDeletedItems,
      ...this.buildingsStore.listWithDeletedItems,
      ...this.zonesStore.listWithDeletedItems,
      ...this.gatesStore.listWithDeletedItems,
      ...this.routesStore.listWithDeletedItems,
      ...this.stagingsStore.listWithDeletedItems,
      ...this.interiorDoorsStore.listWithDeletedItems,
      ...this.interiorPathsStore.listWithDeletedItems,
      ...this.levelsStore.listWithDeletedItems,
      ...this.areasStore.listWithDeletedItems,
      ...this.logisticsObjectsStore.listWithDeletedItems,
      ...this.locationIntegrationsStore.listWithDeletedItems,
      ...this.verticalObjectsStore.listWithDeletedItems,
      ...this.otherLocationsStore.listWithDeletedItems,
    ]
  }

  @computed
  public get isDataReceived() {
    return (
      this.buildingsStore.isDataReceived &&
      this.zonesStore.isDataReceived &&
      this.gatesStore.isDataReceived &&
      this.routesStore.isDataReceived &&
      this.offloadingEquipmentsStore.isDataReceived &&
      this.stagingsStore.isDataReceived &&
      this.interiorDoorsStore.isDataReceived &&
      this.interiorPathsStore.isDataReceived &&
      this.levelsStore.isDataReceived &&
      this.areasStore.isDataReceived &&
      this.logisticsObjectsStore.isDataReceived &&
      this.locationIntegrationsStore.isDataReceived &&
      this.verticalObjectsStore.isDataReceived &&
      this.projectLocationsStore.isDataReceived &&
      this.otherLocationsStore.isDataReceived
    )
  }

  public getById = (id: string): LocationAttributeBase | LocationBase => {
    return this.hierarchyAllAttributesMap[id]
  }

  /**
   * The live root kind of the location with this id, deleted ones included:
   *
   * - the root slug when this client holds the location and it has a kind;
   * - `null` when it holds it but it has none - a location never classified, or a sibling
   *   resource such as equipment;
   * - `undefined` when it does not hold the id at all, so the caller can fall back to
   *   whatever snapshot it has.
   *
   * Reads every store this one routes by type, the catch-all included. A per-store lookup
   * rather than allItemsMapWithDeleted: that map is rebuilt on every read outside a
   * reaction, and this is called per entry.
   */
  public getLocationRootKindById = (id: string): string | null | undefined => {
    const location = this.getByIdWithDeleted(id)

    return location ? getLocationRootKind(location) : undefined
  }

  /**
   * The location with this id, deleted ones included, from whichever store holds it - the
   * catch-all too. Store by store, for the same reason as getLocationRootKindById.
   */
  public getByIdWithDeleted = (id: string): LocationBase | undefined => {
    if (!id) return undefined

    for (const store of Object.values(this.storeByLocationTypeMap)) {
      const location = store?.byId.get(id)
      if (location) return location
    }

    return undefined
  }

  /**
   * Whether a stored SiteLocation is of a kind, judged on the live location: the stored
   * `type` is a snapshot taken when the value was saved, and goes stale when the location
   * is re-kinded. The snapshot decides only when this client does not hold the id.
   *
   * Root-level, like {@link isLocationOfKind}: an elevator is a vertical object.
   */
  public isSiteLocationOfKind(
    siteLocation: ISiteLocation,
    kind: LocationKindValue,
  ): boolean {
    if (!siteLocation) return false

    const liveRootKind = this.getLocationRootKindById(siteLocation.id)
    if (liveRootKind === undefined) {
      return isLocationOfKind({ type: siteLocation.type }, kind)
    }

    return !!liveRootKind && liveRootKind === rootOfKind(kind)
  }

  /**
   * The live location a stored SiteLocation names, found by its id.
   *
   * The value's type is a snapshot from when it was saved, and a location's kind is
   * editable (STX1-597): a zone re-kinded as a building keeps "Zone" in every permit that
   * names it, and a collapsed location carries "Location", which used to throw here. So the
   * type only picks the store asked first - the common case, one direct lookup - and every
   * store is asked by id after it. Ids are unique across stores, so a match by id is the
   * location. Deleted locations stay unresolved, as they always have.
   */
  public getLocationBySiteLocationObj(
    siteLocation: ISiteLocation,
  ): LocationBase {
    if (!siteLocation) return null

    const { id, type } = siteLocation
    return (
      this.storeByLocationTypeMap[type]?.getInstanceById(id) ??
      this.findLiveInstanceById(id)
    )
  }

  // Store by store, not hierarchyAllAttributesMap: that computed is rebuilt on every read
  // outside a reaction, and several callers resolve values in a loop from plain handlers.
  private findLiveInstanceById(id: string): LocationBase {
    for (const store of Object.values(this.storeByLocationTypeMap)) {
      const instance = store.getInstanceById(id)
      if (instance) return instance
    }
    return undefined
  }
}
